import { useMemo } from 'react';
import { ActionIcon, Box, Button, Checkbox, Group } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import {
  redactionChip,
  redactionChipDeselected,
  redactionChipAlreadyRedacted,
} from './styles.css';
import { RedactionChipProps } from './types';
import RedactionIcon from '../RedactionIcon';
import { palette } from '@app/shared/ui-kit/colors';
import { useTranslation } from 'react-i18next';
import RedactionMatchPreview from '../RedactionMatchPreview';
import { RedactEntity } from '@app/types/blackroom-api';
import { TFunction } from 'i18next';
import { GenericRedaction } from '../types';

export const getRedactionText = (
  redaction: Pick<GenericRedaction, 'entity' | 'matchData'>,
  t: TFunction<[string, string], undefined>,
  { short = false } = {},
) => {
  const firstMatch = redaction.matchData[0];

  if (redaction.entity === RedactEntity.Keyword) {
    let text = t('Redaction:Common.keyword');
    if (firstMatch && firstMatch.previewText && firstMatch.rangeInPreview) {
      const [start, length] = firstMatch.rangeInPreview;
      text =
        redaction.matchData[0].previewText?.slice(start, start + length) ?? '';
    }
    return text;
  }

  if (redaction.entity === RedactEntity.Manual) {
    return t('Redaction:Common.redactionEntity.manual');
  }

  return t(
    `Redaction:Common.redactionEntity${short ? 'Short' : ''}.${redaction.entity}`,
  );
};

export const RedactionText = ({
  redaction,
  short = false,
}: {
  redaction: Pick<GenericRedaction, 'entity' | 'matchData'>;
  short?: boolean;
}): string => {
  const { t } = useTranslation();
  return getRedactionText(redaction, t, { short });
};

export default function RedactionChip({
  redaction,
  onClick,
  onDelete,
  onToggle,
  selected,
  disabled,
  withPreview,
  fill,
  shortLabel = true,
  ...groupProps
}: RedactionChipProps) {
  const isDeselected = selected === false;

  const chipClassName = useMemo(
    () =>
      [
        redactionChip,
        isDeselected ? redactionChipDeselected : '',
        disabled ? redactionChipAlreadyRedacted : '',
      ]
        .filter(Boolean)
        .join(' '),
    [isDeselected, disabled],
  );

  let body = (
    <Group
      className={chipClassName}
      {...groupProps}
      wrap="nowrap"
      justify="space-between"
      onClick={onToggle && !disabled ? () => onToggle(redaction) : undefined}
      style={onToggle && !disabled ? { cursor: 'pointer' } : undefined}
    >
      <Group wrap="nowrap" gap={10}>
        {disabled && <IconCheck size={14} color={palette.blackroomGreen} />}
        {onToggle !== undefined && !disabled && (
          <Checkbox
            checked={selected}
            onChange={() => onToggle(redaction)}
            onClick={(e) => e.stopPropagation()}
            size="xs"
            color="blackroomBlack"
          />
        )}
        <RedactionIcon
          entity={redaction.entity}
          color={disabled ? palette.blackroomGray : palette.blackroomLightGray2}
        />
        <Box
          fz={12}
          c={disabled ? palette.blackroomGray : palette.blackroomBlack}
        >
          <RedactionText redaction={redaction} short={shortLabel} />
        </Box>
      </Group>
      <Group wrap="nowrap" gap={10} align="center">
        {redaction.matchData.length > 0 && (
          <Box fw="bolder" c={disabled ? palette.blackroomGray : undefined}>
            x{redaction.matchData.length}
          </Box>
        )}
        {onDelete && (
          <ActionIcon
            variant="subtle"
            color="blackroomRed"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(redaction);
            }}
            aria-label="Delete redaction"
          >
            <IconX size={16} />
          </ActionIcon>
        )}
      </Group>
    </Group>
  );

  if (onClick) {
    body = (
      <Button
        onClick={() => onClick(redaction)}
        variant="transparent"
        p={0}
        style={{ borderWidth: 0 }}
        styles={{
          inner: {
            width: fill ? '100%' : undefined,
          },
          label: {
            width: fill ? '100%' : undefined,
          },
        }}
      >
        {body}
      </Button>
    );
  }

  return withPreview &&
    redaction.matchData.length &&
    redaction.entity !== RedactEntity.Keyword &&
    redaction.entity !== RedactEntity.Manual ? (
    <RedactionMatchPreview matches={redaction.matchData} target={body} />
  ) : (
    body
  );
}
